import { useEffect, useState } from "react";
import axios from "axios";
import { Download } from "lucide-react";
import { API, fmtErr, money } from "../lib/pos";
import { DateRangeField } from "../components/DateField";

export function DailySummary() {
  const today = new Date().toISOString().slice(0, 10);
  const [start, setStart] = useState(today);
  const [end, setEnd] = useState(today);
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [preset, setPreset] = useState("today");

  const load = async () => {
    setBusy(true); setError(null);
    try {
      const params = { start, end };
      const r = await axios.get(`${API}/reports/daily-summary`, { params });
      setData(r.data);
    } catch (e) { setError(fmtErr(e)); setData(null); }
    finally { setBusy(false); }
  };
  useEffect(() => { load(); }, []);

  const applyPreset = (kind) => {
    const now = new Date();
    const iso = (d) => d.toISOString().slice(0, 10);
    let s = today, e = today;
    if (kind === "yesterday") {
      const y = new Date(now.getTime() - 86400000);
      s = iso(y); e = iso(y);
    } else if (kind === "7days") {
      s = iso(new Date(now.getTime() - 6 * 86400000)); e = today;
    } else if (kind === "30days") {
      s = iso(new Date(now.getTime() - 29 * 86400000)); e = today;
    } else if (kind === "mtd") {
      s = iso(new Date(now.getFullYear(), now.getMonth(), 1)); e = today;
    }
    setPreset(kind); setStart(s); setEnd(e);
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [start, end]);

  const exportCsv = () => {
    if (!data) return;
    const rows = [];
    rows.push(["Section", "Key", "Value"]);
    rows.push(["Range", "Start", data.range.start || ""]);
    rows.push(["Range", "End", data.range.end || ""]);
    rows.push(["Totals", "Orders", data.totals.orders]);
    rows.push(["Totals", "Items", data.totals.items]);
    rows.push(["Totals", "Sales (₹)", data.totals.sales]);
    rows.push(["Totals", "Discount (₹)", data.totals.discount]);
    rows.push(["Totals", "Cancellations · events", data.totals.cancellations.events]);
    rows.push(["Totals", "Cancellations · items voided", data.totals.cancellations.items_voided]);
    rows.push(["Totals", "Cancellations · orders voided", data.totals.cancellations.orders_voided]);
    rows.push(["Totals", "Cancellations · amount (₹)", data.totals.cancellations.amount]);
    rows.push([]);
    rows.push(["Payments", "Method", "Count", "Amount (₹)", "Share (%)"]);
    (data.payments || []).forEach((p) => rows.push(["Payments", p.method, p.count, p.amount, p.share]));
    rows.push([]);
    rows.push(["Stations", "Name", "GST", "Orders", "Items", "Sales (₹)", "Discount (₹)"]);
    (data.stations || []).forEach((s) => rows.push(["Stations", s.name, s.gst_number || "", s.orders, s.items, s.sales, s.discount]));
    const csv = rows.map((r) => r.map((x) => {
      const v = x == null ? "" : String(x);
      return v.includes(",") || v.includes("\"") ? '"' + v.replace(/"/g, '""') + '"' : v;
    }).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    const label = start === end ? start : `${start}_${end}`;
    a.download = `daily-summary-${label}.csv`;
    a.click();
    setTimeout(() => URL.revokeObjectURL(a.href), 500);
  };

  return (
    <div className="workspace summary-workspace">
      <div className="page-head">
        <div>
          <p className="eyebrow">MANAGER TOOLS / DAILY SUMMARY</p>
          <h1>Daily summary</h1>
          <p className="muted">Station-wise sales, payment breakdown, discounts and cancellations for any date or range.</p>
        </div>
        <button data-testid="summary-export-csv" className="outline" onClick={exportCsv} disabled={!data}>
          <Download size={14} />Export CSV
        </button>
      </div>

      <div className="date-controls">
        <div className="quick-btns" data-testid="summary-quick-presets">
          {[["today", "Today"], ["yesterday", "Yesterday"], ["7days", "Last 7 days"], ["30days", "Last 30 days"], ["mtd", "This month"]].map(([k, l]) => (
            <button key={k} data-testid={`summary-preset-${k}-button`} className={preset === k ? "active" : ""} onClick={() => applyPreset(k)}>{l}</button>
          ))}
        </div>
        <label className="field">RANGE
          <DateRangeField
            start={start} end={end}
            onChange={({ start: s, end: e }) => { setPreset(""); setStart(s); setEnd(e); }}
            testPrefix="summary"
          />
        </label>
      </div>

      {error && <p className="error" data-testid="summary-error">{error}</p>}
      {busy && !data && <p className="muted">Loading…</p>}

      {data && (
        <>
          <div className="summary-hero" data-testid="summary-hero">
            <div className="hero-card"><small>Total sales</small><b className="green" data-testid="summary-total-sales">{money(data.totals.sales)}</b><em style={{ display: "block", fontSize: 11, color: "var(--muted)", marginTop: 4, fontStyle: "normal" }}>Item + GST</em></div>
            <div className="hero-card"><small>Item value</small><b data-testid="summary-total-item">{money(data.totals.item_total || 0)}</b></div>
            <div className="hero-card"><small>GST</small><b data-testid="summary-total-gst">{money(data.totals.gst_total || 0)}</b></div>
            <div className="hero-card"><small>Orders</small><b data-testid="summary-total-orders">{data.totals.orders}</b></div>
            <div className="hero-card"><small>Items sold</small><b data-testid="summary-total-items">{data.totals.items}</b></div>
            <div className="hero-card"><small>Total discount</small><b className="gold" data-testid="summary-total-discount">-{money(data.totals.discount)}</b></div>
            <div className="hero-card">
              <small>Cancellations</small>
              <b className="red" data-testid="summary-cancel-amount">-{money(data.totals.cancellations.amount)}</b>
              <p className="muted" style={{ fontSize: 11, letterSpacing: 1, textTransform: "uppercase", margin: "4px 0 0" }}>
                {data.totals.cancellations.events} events · {data.totals.cancellations.items_voided} items · {data.totals.cancellations.orders_voided} orders
              </p>
            </div>
          </div>

          <div className="summary-grid">
            <div className="summary-panel">
              <div className="section-line"><h2>Station-wise sales</h2><span className="muted">{data.stations.length} stations</span></div>
              {data.stations.length === 0 ? (
                <p className="muted" style={{ fontSize: 12, marginTop: 10 }}>No paid orders in this range.</p>
              ) : (
                <table className="summary-table" data-testid="summary-station-table">
                  <thead><tr><th>Station</th><th>Orders</th><th>Items</th><th>Item value</th><th>GST</th><th>Sales</th><th>Discount</th></tr></thead>
                  <tbody>
                    {data.stations.map((s) => (
                      <tr key={s.id} data-testid={`summary-station-row-${s.id}`}>
                        <td>
                          <b>{s.name}</b>
                          {s.gst_number && <small>GST · {s.gst_number}</small>}
                        </td>
                        <td className="n">{s.orders}</td>
                        <td className="n">{s.items}</td>
                        <td className="n">{money(s.item_total || 0)}</td>
                        <td className="n">{money(s.gst_total || 0)}</td>
                        <td className="n">{money(s.sales)}</td>
                        <td className="n">{s.discount > 0 ? `-${money(s.discount)}` : "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            <div className="summary-panel">
              <div className="section-line"><h2>Payment method</h2><span className="muted">{(data.payments || []).length} method(s)</span></div>
              {(data.payments || []).length === 0 ? (
                <p className="muted" style={{ fontSize: 12, marginTop: 10 }}>No payments recorded.</p>
              ) : (
                <div data-testid="summary-payments">
                  {data.payments.map((p) => (
                    <div key={p.method} className="pay-bar" data-testid={`summary-payment-${p.method}`}>
                      <span style={{ width: `${Math.max(4, p.share)}%` }} />
                      <b>{p.method.toUpperCase()} · {money(p.amount)} · {p.share.toFixed(1)}%</b>
                    </div>
                  ))}
                </div>
              )}
              <div className="section-line" style={{ marginTop: 22 }}><h2>Cancellations</h2><span className="muted">breakdown</span></div>
              <table className="summary-table" style={{ marginTop: 6 }}>
                <tbody>
                  <tr><td>Events</td><td className="n" data-testid="summary-cancel-events">{data.totals.cancellations.events}</td></tr>
                  <tr><td>Items voided</td><td className="n" data-testid="summary-cancel-items">{data.totals.cancellations.items_voided}</td></tr>
                  <tr><td>Whole orders voided</td><td className="n" data-testid="summary-cancel-orders">{data.totals.cancellations.orders_voided}</td></tr>
                  <tr><td><b>Total voided ₹</b></td><td className="n" style={{ color: "var(--red)" }}>{money(data.totals.cancellations.amount)}</td></tr>
                </tbody>
              </table>
            </div>
          </div>

          <div className="summary-panel" style={{ marginTop: 20 }} data-testid="summary-hourly-panel">
            <div className="section-line">
              <h2>Hour-of-day heatmap</h2>
              <span className="muted">IST · sales at hour of payment</span>
            </div>
            {(() => {
              const bars = data.hourly || [];
              const peak = Math.max(1, ...bars);
              const total = bars.reduce((a, b) => a + b, 0);
              if (total === 0) {
                return <p className="muted" style={{ fontSize: 12, marginTop: 10 }}>No payments in this range.</p>;
              }
              return (
                <div className="heatmap" data-testid="summary-heatmap">
                  {bars.map((v, h) => (
                    <div className="heat-col" key={h} data-testid={`heat-col-${h}`} title={`${String(h).padStart(2, "0")}:00 · ${money(v)}`}>
                      <div className="heat-bar" style={{ height: `${v > 0 ? Math.max(4, (v / peak) * 100) : 2}%`, opacity: v > 0 ? Math.max(0.35, v / peak) : 0.12 }}>
                        {v > 0 && <b>{money(v).replace("₹", "")}</b>}
                      </div>
                      <em>{String(h).padStart(2, "0")}</em>
                    </div>
                  ))}
                </div>
              );
            })()}
          </div>
        </>
      )}
    </div>
  );
}

/* ---------- Reports ---------- */
